/*
 * Injects the "₹1 deal" banner into GoKwik's side cart.
 *
 * WHY THIS EXISTS
 * GoKwik replaces the theme's cart for non-B2B customers: the header cart click
 * calls window.openGokwikSideCart() (see dd-custom.js) and GoKwik renders its
 * panel inside <shadow-dom-container>'s shadow root. Theme CSS and Liquid
 * markup cannot cross a shadow boundary, so this script walks into the shadow
 * root and injects the banner, with its own <style>.
 *
 * GoKwik's internal markup is not part of this theme and may change without
 * notice, so nothing here depends on a specific GoKwik class name. Both the
 * host element and the mount point can be pinned without editing this file:
 *
 *   window.ddUnlockGift.hostSelector  = 'shadow-dom-container';
 *   window.ddUnlockGift.mountSelector = '.their-cart-body';
 *
 * Run ddUnlockGiftDebug() in the console to see where it decided to mount.
 *
 * Config comes from snippets/cart-unlock-gift-gokwik.liquid.
 */
(function () {
  if (window.__ddUnlockGiftGokwikInit) return;
  window.__ddUnlockGiftGokwikInit = true;

  var CFG = window.ddUnlockGift;
  if (!CFG || !CFG.threshold || !CFG.url) return;

  var BANNER_ID = 'dd-unlock-gift-banner';
  var STYLE_ID = 'dd-unlock-gift-style';
  var HOST_SELECTOR = CFG.hostSelector || 'shadow-dom-container';
  var MAX_SHADOW_DEPTH = 6;

  var cartTotal = null;
  var eligible = false;
  var shortfall = 0;

  /* The offer's own products must not count toward their own threshold. */
  var EXCLUDED = Object.create(null);
  (CFG.excludeVariantIds || []).forEach(function (id) { EXCLUDED[String(id)] = true; });

  function money(paise) {
    return '₹' + Math.ceil(paise / 100);
  }

  function eligibleTotal(cart) {
    return (cart.items || []).reduce(function (sum, item) {
      if (EXCLUDED[String(item.variant_id)] || EXCLUDED[String(item.id)]) return sum;
      var line = item.final_line_price;
      if (typeof line !== 'number') line = (item.price || 0) * (item.quantity || 0);
      return sum + line;
    }, 0);
  }

  var hostCache = null;
  var observed = [];
  var refreshTimer = null;
  var syncTimer = null;
  var docTimer = null;
  var selfMutating = false;

  /* ---------------- shadow root discovery ---------------- */

  /*
   * Scoped to GoKwik's host on purpose. A document-wide sweep for shadow roots
   * would run on every mutation of a very busy page; if the host is missing,
   * GoKwik simply isn't here and there is nothing to do.
   */
  function gokwikRoot() {
    if (hostCache && hostCache.isConnected && hostCache.shadowRoot) {
      return hostCache.shadowRoot;
    }
    hostCache = null;

    var host = document.querySelector(HOST_SELECTOR);
    if (host && host.shadowRoot) {
      hostCache = host;
      return host.shadowRoot;
    }
    return null;
  }

  function collectShadowRoots(root, acc, depth) {
    if (!root || depth > MAX_SHADOW_DEPTH) return acc;

    var nodes;
    try {
      nodes = root.querySelectorAll('*');
    } catch (e) {
      return acc;
    }

    for (var i = 0; i < nodes.length; i++) {
      var sr = nodes[i].shadowRoot;
      if (sr && acc.indexOf(sr) === -1) {
        acc.push(sr);
        collectShadowRoots(sr, acc, depth + 1);
      }
    }
    return acc;
  }

  function shadowRoots() {
    var root = gokwikRoot();
    if (!root) return [];
    return collectShadowRoots(root, [root], 0);
  }

  /* ---------------- placement ---------------- */

  function isVisible(el) {
    if (!el || !el.getBoundingClientRect) return false;
    var r = el.getBoundingClientRect();
    return r.width > 120 && r.height > 80;
  }

  function hasBox(el) {
    if (!el || !el.getBoundingClientRect) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  /* Deepest VISIBLE element whose text matches. */
  function findByText(root, re) {
    var all;
    try { all = root.querySelectorAll('*'); } catch (e) { return null; }

    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.id === BANNER_ID || (el.closest && el.closest('#' + BANNER_ID))) continue;
      if (!re.test(el.textContent || '')) continue;

      var deeper = false;
      for (var j = 0; j < el.children.length; j++) {
        if (re.test(el.children[j].textContent || '')) { deeper = true; break; }
      }
      if (!deeper && hasBox(el)) return el;
    }
    return null;
  }

  function childOf(container, node) {
    var n = node;
    while (n && n.parentElement !== container) n = n.parentElement;
    return n;
  }

  function commonAncestor(a, b) {
    var n = a;
    while (n && !n.contains(b)) n = n.parentElement;
    return n;
  }

  /*
   * A chrome row is a SHORT bar with no image and one short sentence: the
   * "Your Cart" header, the shipping banner, its progress line. Product rows
   * also say "Free Shipping" (the badge) but carry a thumbnail and much more
   * text, which is what keeps them out.
   */
  var CHROME_RE = /your\s*cart|shipping/i;

  function isChromeRow(el) {
    if (!el || !CHROME_RE.test(el.textContent || '')) return false;
    if (el.getBoundingClientRect().height > 150) return false;
    if (el.querySelector('img, picture, video')) return false;
    return (el.textContent || '').replace(/\s+/g, ' ').trim().length <= 80;
  }

  /*
   * From the header row, step over any further chrome bars. If the next block
   * is tall but STARTS with chrome (GoKwik nesting the shipping bar inside the
   * scroll body), step into it and continue there.
   */
  function walkChrome(row) {
    var after = row;
    var next = row.nextElementSibling;
    var hops = 0;

    while (next && hops < 4) {
      if (isChromeRow(next)) {
        after = next;
        next = next.nextElementSibling;
        hops++;
        continue;
      }
      var lead = next.firstElementChild;
      if (lead && CHROME_RE.test(next.textContent || '') && isChromeRow(lead)) {
        return walkChrome(lead);
      }
      break;
    }
    return { parent: after.parentElement, after: after };
  }

  /*
   * Returns { parent, after }: insert into `parent`, right after `after`
   * (or as first child when `after` is null). Returns null when there is
   * nothing VISIBLE to anchor to — i.e. the drawer is closed.
   *
   * Anchoring on the header rather than on cart items is what keeps the
   * banner in the same place on an EMPTY cart: with no items, an item-based
   * mount lands on the panel itself and puts the banner above the header.
   */
  function findPlacement(root) {
    if (CFG.mountSelector) {
      try {
        var pinned = root.querySelector(CFG.mountSelector);
        if (pinned && isVisible(pinned)) return { parent: pinned, after: null };
      } catch (e) {}
    }

    var header = findByText(root, /your\s*cart/i);
    if (header) {
      var shipping = findByText(root, /free\s*shipping/i);
      var panel = shipping ? commonAncestor(header, shipping) : header.parentElement;
      var row = panel && childOf(panel, header);
      if (row) return walkChrome(row);
    }

    /*
     * Header not found — fall back to a VISIBLE cart-ish container.
     *
     * Never a blind "first child of the root": GoKwik's <shadow-dom-container>
     * lives in the body at all times and is zero-height while the cart is
     * closed. Mounting into its skeleton gave it a real height, so the banner
     * painted as a ~109px block at the top of every page. The banner belongs
     * inside an OPEN panel or nowhere.
     */
    var selectors = ['[class*="cart-item" i]', '[class*="cart-body" i]', '[class*="cart" i]', '[class*="drawer" i]'];
    for (var i = 0; i < selectors.length; i++) {
      var matches;
      try { matches = root.querySelectorAll(selectors[i]); } catch (e) { continue; }
      for (var j = 0; j < matches.length; j++) {
        if (isVisible(matches[j])) return { parent: matches[j].parentElement || matches[j], after: null };
      }
    }
    return null;
  }

  function isPlaced(existing, place) {
    if (!existing || existing.parentElement !== place.parent) return false;
    return place.after
      ? existing.previousElementSibling === place.after
      : place.parent.firstElementChild === existing;
  }

  /* ---------------- banner markup + styles ---------------- */

  function ensureStyle(root) {
    if (root.querySelector('#' + STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      /* Same design language as the collection-page widget: icon tile,
         SPECIAL OFFER label, red amount accent, navy CTA. */
      '#' + BANNER_ID + '{',
      '  display:flex;align-items:center;gap:12px;',
      '  margin:12px;padding:12px 14px;box-sizing:border-box;',
      '  background:#fff;border:1px solid #e6e8ec;border-radius:12px;',
      '  font-family:inherit;line-height:1.35;',
      '}',
      '#' + BANNER_ID + ' .ddug-icon{',
      '  flex:0 0 auto;width:38px;height:38px;border-radius:10px;background:#eef2ff;',
      '  display:flex;align-items:center;justify-content:center;font-size:18px;line-height:1;',
      '}',
      '#' + BANNER_ID + ' .ddug-body{flex:1 1 auto;min-width:0;}',
      '#' + BANNER_ID + ' .ddug-label{',
      '  display:block;font-size:9.5px;font-weight:700;letter-spacing:.09em;',
      '  text-transform:uppercase;color:#8b93a5;margin-bottom:2px;',
      '}',
      '#' + BANNER_ID + ' .ddug-text{margin:0;font-size:13px;font-weight:700;color:#111827;}',
      '#' + BANNER_ID + ' .ddug-text b{color:#e11d2f;font-weight:800;}',
      '#' + BANNER_ID + ' .ddug-link{',
      '  flex:0 0 auto;display:inline-flex;align-items:center;gap:5px;',
      '  padding:9px 14px;border-radius:8px;background:#14224e;color:#fff;',
      '  font-size:12px;font-weight:700;text-decoration:none;white-space:nowrap;',
      '}',
      '#' + BANNER_ID + ' .ddug-link:hover{background:#0E1F4D;}',
      '#' + BANNER_ID + '[data-state="unlocked"]{background:#eef9f0;border-color:#b7e2c3;}',
      '#' + BANNER_ID + '[data-state="unlocked"] .ddug-icon{background:#dff3e5;}',
      '@media screen and (max-width:420px){',
      '  #' + BANNER_ID + '{flex-wrap:wrap;gap:10px;}',
      '  #' + BANNER_ID + ' .ddug-body{flex:1 1 calc(100% - 50px);}',
      '  #' + BANNER_ID + ' .ddug-link{margin-left:50px;}',
      '}'
    ].join('\n');

    root.appendChild(style);
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* Red accent on the first rupee amount — ₹299 when locked, ₹1 when unlocked.
     Escaped first: the unlocked message is a theme setting. */
  function accent(text) {
    return escapeHtml(text).replace(/(₹\s?[\d,]+(?:\.\d+)?)/, '<b>$1</b>');
  }

  function lockedCopy() {
    return (CFG.lockedMessage || 'Add %%AMOUNT%% more to unlock your ₹1 deal')
      .replace('%%AMOUNT%%', money(shortfall));
  }

  function buildBanner(unlocked) {
    var wrap = document.createElement('div');
    wrap.id = BANNER_ID;
    wrap.setAttribute('role', 'status');
    wrap.setAttribute('data-state', unlocked ? 'unlocked' : 'locked');

    var icon = document.createElement('span');
    icon.className = 'ddug-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = unlocked ? '🎉' : '🎁';

    var body = document.createElement('div');
    body.className = 'ddug-body';

    var label = document.createElement('span');
    label.className = 'ddug-label';
    label.textContent = CFG.label || 'SPECIAL OFFER';

    var text = document.createElement('p');
    text.className = 'ddug-text';
    text.innerHTML = accent(unlocked ? CFG.message : lockedCopy());

    body.appendChild(label);
    body.appendChild(text);
    wrap.appendChild(icon);
    wrap.appendChild(body);

    /* Locked: no button on purpose — the products cannot be added yet. */
    if (unlocked) {
      var link = document.createElement('a');
      link.className = 'ddug-link';
      link.href = CFG.url;
      link.textContent = CFG.buttonLabel + ' ›';
      wrap.appendChild(link);
    }
    return wrap;
  }

  /* ---------------- sync ---------------- */

  function syncNow() {
    /* Nothing known yet — leave whatever is on screen alone. */
    if (cartTotal === null) return;

    shadowRoots().forEach(function (root) {
      var existing = root.querySelector('#' + BANNER_ID);
      var place = findPlacement(root);

      /* No open panel to mount into. A banner hidden with the drawer is left
         alone (re-checked on open); one that is VISIBLE right now is in the
         wrong place and comes out. */
      if (!place) {
        if (existing && hasBox(existing)) existing.remove();
        return;
      }

      /* Shown on an EMPTY cart too — "Add ₹299 more" is the strongest nudge
         exactly when there is nothing in the cart yet. */
      var wanted = eligible ? 'unlocked' : 'locked';

      /* Already correct and in the right place — leave the DOM alone. */
      if (existing && existing.getAttribute('data-state') === wanted && isPlaced(existing, place)) {
        if (!eligible) {
          var p = existing.querySelector('.ddug-text');
          var next = accent(lockedCopy());
          if (p && p.innerHTML !== next) p.innerHTML = next;
        }
        return;
      }

      if (existing) existing.remove();
      ensureStyle(root);
      var before = place.after ? place.after.nextElementSibling : place.parent.firstElementChild;
      place.parent.insertBefore(buildBanner(eligible), before);
    });
  }

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      /* Our own writes re-enter the observer below; flag them so it doesn't loop. */
      selfMutating = true;
      try {
        syncNow();
      } catch (e) {
        /* never let a GoKwik markup change break the page */
      } finally {
        setTimeout(function () { selfMutating = false; }, 0);
      }
    }, 120);
  }

  function refreshTotal() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      (window.ddCart && window.ddCart.get
        ? window.ddCart.get()
        : fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); })
      )
        .then(function (cart) {
          if (!cart) return;
          cartTotal = cart.total_price;

          var counted = eligibleTotal(cart);
          eligible = counted >= CFG.threshold;
          shortfall = eligible ? 0 : CFG.threshold - counted;

          scheduleSync();
        })
        .catch(function () {});
    }, 250);
  }

  /* ---------------- wiring ---------------- */

  function watchShadowRoots() {
    shadowRoots().forEach(function (root) {
      if (observed.indexOf(root) !== -1) return;
      observed.push(root);

      /*
       * Quantity edits happen inside GoKwik's panel and never reach the theme's
       * pub/sub, so panel mutations are the signal. Attributes as well as
       * children: GoKwik can open the drawer with nothing but a class flip, and
       * an EMPTY cart renders no new children on open — a childList-only
       * observer never fired and the banner never appeared.
       */
      new MutationObserver(function () {
        if (selfMutating) return;
        refreshTotal();
        scheduleSync();
      }).observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'open']
      });
    });
  }

  /* The theme opens the drawer via window.openGokwikSideCart() (dd-custom.js).
     Wrap it and sync a few times to ride out the open animation. GoKwik defines
     it asynchronously and may redefine it, so re-check on every document tick. */
  function hookOpen() {
    var orig = window.openGokwikSideCart;
    if (typeof orig !== 'function' || orig.__ddUnlockHooked) return;

    var wrapped = function () {
      var result = orig.apply(this, arguments);
      [150, 500, 1200].forEach(function (ms) {
        setTimeout(function () { refreshTotal(); scheduleSync(); }, ms);
      });
      return result;
    };
    wrapped.__ddUnlockHooked = true;
    window.openGokwikSideCart = wrapped;
  }

  /* GoKwik mounts its host asynchronously; wait for it to show up. */
  new MutationObserver(function () {
    clearTimeout(docTimer);
    docTimer = setTimeout(function () {
      hookOpen();
      watchShadowRoots();
      scheduleSync();
    }, 200);
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refreshTotal);
  }
  document.addEventListener('cart:updated', refreshTotal);

  hookOpen();
  watchShadowRoots();
  refreshTotal();

  /* ---------------- debug helper ---------------- */

  window.ddUnlockGiftDebug = function () {
    var roots = shadowRoots();
    console.log('[unlock-gift] config:', CFG);
    console.log('[unlock-gift] host selector:', HOST_SELECTOR, '→', document.querySelector(HOST_SELECTOR));
    console.log('[unlock-gift] cart total (paise):', cartTotal, '| threshold:', CFG.threshold);
    console.log('[unlock-gift] eligible:', eligible, eligible ? '' : '(short by ' + money(shortfall) + ')');
    console.log('[unlock-gift] excluded offer variants:', Object.keys(EXCLUDED).length);
    console.log('[unlock-gift] shadow roots found:', roots.length);
    roots.forEach(function (root, i) {
      var place = findPlacement(root);
      console.log('  root[' + i + '] host:', root.host && root.host.tagName,
                  '→ placed in:', place && place.parent, '| after:', place && place.after);
    });
    return roots;
  };
})();
